import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown, ChevronUp, Search } from 'lucide-react';

interface FilterableItem {
  date: string;
  title: string;
  tags?: string[];
}

interface FiltersProps<T extends FilterableItem> {
  items: T[];
  onFilteredItemsChange: (items: T[]) => void;
  placeholderText?: string;
}

const Filters = <T extends FilterableItem>({items, onFilteredItemsChange, placeholderText = "Search..."}: FiltersProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const allTags = Array.from(new Set(items.flatMap((item) => item.tags || []).filter((tag) => tag)));

  useEffect(() => {
    let filtered = [...items];

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter((item) =>
        selectedTags.every((tag) => item.tags?.includes(tag) ?? false)
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    onFilteredItemsChange(filtered);
  }, [items, sortOrder, selectedTags, searchTerm, onFilteredItemsChange]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  };

  return (
    <div className="mb-8">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow">
        <Filter size={20} />
        <span></span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input type="text" placeholder={placeholderText} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"/>
            </div>
          </div>
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Sort by date</h3>
            <div className="flex gap-2">
              <button onClick={() => setSortOrder('newest')} className={`px-3 py-1 rounded-lg ${sortOrder === 'newest' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}>
                Newest first
              </button>
              <button onClick={() => setSortOrder('oldest')} className={`px-3 py-1 rounded-lg ${sortOrder === 'oldest' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}>
                Oldest first
              </button>
            </div>
          </div>
          {allTags.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Filter by tags</h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button key={tag} onClick={() => toggleTag(tag)} className={`px-3 py-1 rounded-lg ${selectedTags.includes(tag) ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Filters;
